/* eslint-disable @typescript-eslint/naming-convention */
import React, { Fragment, useEffect, useState } from "react";

import {
  CircularProgress,
  FormControl,
  FormHelperText,
  OutlinedInputProps,
  Paper,
  PaperProps,
  TextField,
  AutocompleteGetTagProps,
} from "@mui/material";
import withStyles from "@mui/styles/withStyles";
import {
  Close as CloseIcon,
  InfoOutlined as InfoOutlinedIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { Autocomplete } from "@mui/material";

import {
  AutocompleteChangeReason,
  AutocompleteInputChangeReason,
  FilterOptionsState,
} from "@mui/material/useAutocomplete";

import { AxiosResponse } from "axios";
import { AiOutlinePlusCircle } from "react-icons/ai";

import { getBrandCustom } from "../../../utils/getCustomColor";
import Button, { Color } from "../Button";
import Chip from "../Chip";
import useStyles from "./styles";
import { colors } from "@zapalacorp/tokens";

export interface AutoSuggestProps<T> {
  ref?: React.Ref<unknown> | undefined;
  dataCy?: string;
  label?: string;
  name?: string;
  error?: boolean;
  errorMessage?: string;
  disableClearable?: boolean;
  clearOptionOnEscape?: boolean;
  rightIcon?: JSX.Element;
  clearOptionsOnEscape?: boolean;
  placeholder?: string;
  onBlur?: (value: any) => void;
  onChange?: (value: any, reason: AutocompleteChangeReason) => void;
  onChangeInput?: (value: string, reason: AutocompleteInputChangeReason) => void;
  value?: any;
  defaultValue?: any;
  multiple?: boolean;
  freeSolo?: boolean;
  removeShadowBox?: boolean;
  options?: T[];
  inputValue?: string;
  disabled?: boolean;
  onSearch?: (value: string) => Promise<AxiosResponse<any>>;
  getOptionLabel?: (option: T | string) => string;
  getOptionSelected?: (option: T, value: T) => boolean;
  filterOptions?: (options: T[], state: FilterOptionsState<T>) => T[];
  getOptionDisabled?: (option: T) => boolean;
  noOptionsText?: React.ReactNode;
  closeIcon?: React.ReactNode;
  autoSelect?: boolean;
  autoHighlight?: boolean;
  tagDataCy?: string;
  bordercolor?: string;
  required?: boolean;
  renderOption?: any;
  InputProps?: Partial<OutlinedInputProps>;
  fillInputValueOnChange?:
    | ((value: string, reason: AutocompleteInputChangeReason) => boolean)
    | boolean;
  addOptionTitle?: string;
  onAddOption?: () => void;
  limitTags?: number;
}

const TextFieldCustomized = withStyles({
  root: {
    "& .MuiOutlinedInput-root": {
      backgroundColor: (props: { disabled?: boolean }) => (props?.disabled ? "#F9FAFB" : "#FFFFFF"),
      minHeight: "40px",
      minWidth: "250px",
      borderRadius: "8px",
      fontFamily: "Manrope",
      fontWeight: 500,
      padding: 0,
      paddingLeft: "6px",
      fontSize: "14px",
      lineHeight: "24px",
      color: "#667085",
      "& .MuiAutocomplete-popupIndicatorOpen": {
        transform: "none",
      },
      "& fieldset": {
        borderColor: (props: { error?: boolean; bordercolor?: string }) =>
          props?.error ? props?.bordercolor : "#C4C4C4",
      },
      "&:hover fieldset": {
        borderColor: (props: { bordercolor?: string; disabled?: boolean }) =>
          !props?.disabled ? props?.bordercolor : "#C4C4C4",
      },
      "&.Mui-focused fieldset": {
        borderColor: (props: { bordercolor?: string }) => props?.bordercolor,
      },
    },
  },
})(TextField);

const FormControlCustomized = withStyles(() => ({
  root: {
    width: "100%",
    display: "flex",
    justifyContent: "flex-end",
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: (props: { bordercolor?: string; disabled?: boolean }) =>
        !props?.disabled ? props?.bordercolor : "#C4C4C4",
    },
    "&:active .MuiOutlinedInput-notchedOutline": {
      borderColor: (props: { bordercolor?: string; disabled?: boolean }) =>
        !props?.disabled ? props?.bordercolor : "#C4C4C4",
    },
  },
}))(FormControl);

const PaperCustomized = withStyles(() => ({
  root: {
    borderRadius: "8px",
    fontFamily: "Manrope",
    fontWeight: 500,
    color: "#667085",
    boxShadow: (props: { freeSolo?: boolean }) => (props?.freeSolo ? "none" : "12px"),
    "& .MuiAutocomplete-listbox": {
      "& li": {
        paddingTop: "8px",
        paddingBottom: "8px",
      },
      "& :hover": {
        backgroundColor: "#EDF5FB",
        color: getBrandCustom("primaryColor", colors.brandBlue500),
      },
    },
  },
}))(Paper);

const AutoSuggest = <T extends unknown>(props: AutoSuggestProps<T>) => {
  const {
    label,
    name,
    value = "",
    inputValue = "",
    errorMessage,
    disableClearable,
    onChangeInput,
    error,
    placeholder,
    onChange,
    freeSolo,
    removeShadowBox = true,
    multiple,
    onSearch,
    getOptionSelected,
    getOptionLabel,
    options,
    disabled,
    filterOptions,
    rightIcon,
    closeIcon,
    clearOptionsOnEscape,
    dataCy,
    tagDataCy,
    fillInputValueOnChange = true,
    required,
    addOptionTitle,
    onAddOption,
    renderOption,
    InputProps,
    getOptionDisabled,
    limitTags,
    ...rest
  } = props;

  const classes = useStyles({ disabled });

  const [isOpen, setIsOpen] = useState<boolean>(false);

  const [_options, setOptions] = useState<T[]>([]);
  const [typingTimeout, setTypingTimeout] = useState<NodeJS.Timeout>();
  const [loading, setLoading] = useState<boolean>(false);
  const [_inputValue, setInputValue] = useState<string>("");

  useEffect(() => {
    setInputValue(inputValue || "");
  }, [inputValue]);

  useEffect(() => {
    setOptions(options || []);
  }, [options]);

  const requestNewSuggests = async (search: string, reason: AutocompleteInputChangeReason) => {
    if (onSearch && getOptionLabel && getOptionSelected && search && reason === "input") {
      try {
        setLoading(true);
        const results = await onSearch(search);

        if (results?.data?.rows) setOptions(results.data.rows);
        else if (results?.data?.data) setOptions(results.data.data);
        else if (results?.data) setOptions(results.data);
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    }
  };

  const onInputChange = (value: string, reason: AutocompleteInputChangeReason) => {
    const fill =
      fillInputValueOnChange instanceof Function
        ? fillInputValueOnChange(value, reason)
        : fillInputValueOnChange;

    if (onChangeInput) {
      onChangeInput(value, reason);
    } else if (fill) {
      setInputValue(value);
    }
    if (typingTimeout) clearTimeout(typingTimeout);
    setTypingTimeout(setTimeout(() => requestNewSuggests(value, reason), 500));
  };

  const renderTags = (array: T[], getTagProps: AutocompleteGetTagProps) => {
    // eslint-disable-next-line array-callback-return
    return array.map((value, index) => {
      if (getOptionLabel) {
        return (
          <Chip
            {...getTagProps({ index })}
            disabled={false}
            style={{
              borderRadius: "4px",
              backgroundColor: "#EDF5FB",
              color: getBrandCustom("primaryColor", colors.brandBlue500),
              fontFamily: "Manrope",
              fontSize: "12px",
              lineHeight: "20px",
              fontWeight: 600,
            }}
            data-cy={`${tagDataCy}-${getOptionLabel(value)}`}
            label={<span>{getOptionLabel(value)}</span>}
            size="small"
            deleteIcon={<CloseIcon className={classes.closeIcon} fontSize="small" />}
          />
        );
      }
    });
  };

  const renderRequiredLabel = () => {
    if (required) {
      return <span className={classes.required}>{"*"}</span>;
    }

    return null;
  };

  const renderLabel = () => {
    if (label) {
      return (
        <span className={classes.label}>
          {label}
          {renderRequiredLabel()}
        </span>
      );
    }

    return null;
  };

  const renderError = () => {
    if (error && errorMessage) {
      return (
        <FormHelperText className={classes.formHelper} data-cy="errors">
          <InfoOutlinedIcon htmlColor="#D92D20" className={classes.iconError} />
          {errorMessage}
        </FormHelperText>
      );
    }
  };

  const renderPaper = (props: PaperProps) => {
    return (
      <PaperCustomized elevation={8} {...props} freeSolo={freeSolo && removeShadowBox}>
        {props.children}
        {addOptionTitle && (
          <div className={classes.addButton}>
            <Button
              onMouseDown={onAddOption}
              startIcon={
                <AiOutlinePlusCircle color={getBrandCustom("primaryColor", colors.brandBlue500)} />
              }
              customColor={Color.SEND}
            >
              {addOptionTitle}
            </Button>
          </div>
        )}
      </PaperCustomized>
    );
  };

  return (
    <FormControlCustomized
      disabled={disabled}
      bordercolor={error ? "#D92D20" : getBrandCustom("primaryColor", colors.brandBlue500)}
    >
      {renderLabel()}
      <Autocomplete
        data-cy={dataCy}
        {...rest}
        disableClearable={disableClearable}
        disabled={disabled}
        multiple={multiple}
        freeSolo={freeSolo}
        fullWidth
        options={_options}
        value={value}
        filterOptions={filterOptions}
        inputValue={_inputValue.replace("  ", " ").trimStart()}
        isOptionEqualToValue={getOptionSelected}
        getOptionLabel={getOptionLabel}
        onOpen={() => setIsOpen(true)}
        onClose={() => {
          if (clearOptionsOnEscape) setOptions([]);
          setIsOpen(false);
        }}
        clearOnEscape={false}
        clearOnBlur={false}
        clearIcon={closeIcon}
        onInputChange={(event, value, reason) => onInputChange(value, reason)}
        onChange={(event, value, reason) => {
          if (onChange) onChange(value, reason);
        }}
        getOptionDisabled={getOptionDisabled}
        renderTags={renderTags}
        PaperComponent={renderPaper}
        renderOption={renderOption}
        popupIcon={
          <SearchIcon
            htmlColor={isOpen ? getBrandCustom("primaryColor", colors.brandBlue500) : "#D0D5DD"}
          />
        }
        limitTags={limitTags}
        renderInput={(params) => (
          <TextFieldCustomized
            data-cy={dataCy}
            {...params}
            name={name}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <Fragment>
                  {loading ? (
                    <CircularProgress className={classes.circularProgress} size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                  {rightIcon}
                </Fragment>
              ),
              ...InputProps,
            }}
            disabled={disabled}
            error={error}
            bordercolor={error ? "#D92D20" : getBrandCustom("primaryColor", colors.brandBlue500)}
            variant="outlined"
            placeholder={disabled ? undefined : placeholder}
          />
        )}
      />
      {renderError()}
    </FormControlCustomized>
  );
};

export default AutoSuggest;
